import { Transition } from '@headlessui/react';
import { useState } from 'react';
import { useBjForm } from '@hooks/useBjForm';
import { useTranslation } from 'next-i18next';
import { CardPaymentList } from '@components/features/payment';
import {
  Input, Button, Label, RadioText,
} from '@components/common/Forms';
import { MY_BESTIE_PRICE_EUR } from '../constants';

interface PaymentMethodsBestieProps {
  cartDetails?: ApiSchema<'UserOrderPayment'>;
  orderCredits: <Data, Args>(arg: Args) => Promise<Data | void>;
  savingOrder?: boolean;
  transitionProps?: object;
}

/**
 * @description Payment methods form for the My Bestie purchase.
 * Fixed at 50 neuro / 50 EUR — no amount input needed.
 * Personal data is pre-validated before this modal opens (see MyBestieLanding).
 */
export const PaymentMethodsBestie = (props: PaymentMethodsBestieProps) => {
  const {
    cartDetails, orderCredits, savingOrder, transitionProps,
  } = props;

  const { t } = useTranslation('common');
  const { record, handleCallbackSubmit } = useBjForm();

  const [selectedMethod, setSelectedMethod] = useState(cartDetails?.paymentTypes?.[0]);
  const [selectedCard, setSelectedCard] = useState(Number(cartDetails?.creditCards?.find(((card) => card.lastActive))?.id) || 0);

  return (
    <form
      noValidate
      onSubmit={(event) => handleCallbackSubmit(event, orderCredits)}
    >
      <Transition
        as="div"
        className="px-6 pt-3"
        {...transitionProps}
      >
        <Input type="hidden" value={selectedCard} {...record('creditCardId', { convertEmptyValue: 'toNumber' })} />
        <Input type="hidden" value={selectedMethod?.id} {...record('paymentTypeId')} />

        <div className="mt-4 border-t pt-4">
          <CardPaymentList
            paymentMethods={cartDetails?.paymentTypes}
            creditsAmount={MY_BESTIE_PRICE_EUR}
            creditCards={cartDetails?.creditCards}
            getSelectedMethod={setSelectedMethod}
            getSelectedCard={setSelectedCard}
            showAvailableCredits={false}
            className="max-w-sm md:!grid-cols-1"
          />
        </div>

        {selectedCard === 0 && (
          <div className="mt-3">
            <Label htmlFor="saveCard" className="flex">
              <Input type="checkbox" {...record('saveCard', { convertValue: 'toBoolean', defaultChecked: true })} />
              <RadioText>{t('payment.save.card.label')}</RadioText>
            </Label>
          </div>
        )}

        {/* Order summary — fixed price */}
        <div className="mt-3 border-y px-4 py-3">
          <div className="flex items-center justify-between py-1">
            <span>{t('mc.personal.agent')}</span>
            <strong className="text-right text-xl text-secondary">
              {MY_BESTIE_PRICE_EUR}
              <span className="ml-1 text-sm">
                &euro;&nbsp;
                {t('anexa.label.includetva')}
              </span>
            </strong>
          </div>
        </div>
      </Transition>

      <div className="justify-center bg-surface px-6 pt-4 md:flex">
        <Button
          type="submit"
          rounding="full"
          color="secondary"
          size="sm"
          className="mb-4 w-full md:order-2 md:w-1/2"
          isLoading={savingOrder}
          disabled={!selectedMethod}
        >
          {t('mc.personal.agent.activate')
            .replace('{amount}', MY_BESTIE_PRICE_EUR.toString())}
        </Button>
      </div>
    </form>
  );
};
